import { Method } from 'axios';

import { FeatureFlags } from '~src/commonInfra/featureFlags/FeatureFlags';

import { Messages } from '../../codegen/Localize';
import { openReactConfirmationDialog, ReactConfirmationDialogOptions } from '../../commonInfra/dialogs/ReactConfirmationDialog';
import { TabDialogWidth } from '../../commonInfra/dialogs/style/TabDialogWidth';
import { L10n } from '../../commonInfra/L10n/L10n';
import { ToasterService, ToastType } from '../../commonInfra/services/ToasterService';
import { makeRestApiRequest, RestApiRequestConfig } from '../../httpClient/RestApiService';
import { DiscardDraftRequestedAction } from '../actions/DiscardDraftAction';
import { FetchDraftByParentIdRequestedAction,FetchDraftsRequestedAction } from '../actions/DraftsActions';
import { Draft,GetDraftsRequestBody} from '../types/DraftsTypes';
import { WorkbookDraftDraftRestApiVersion } from './DraftsRestVersion';

export const DraftsRequests = {
  getDraftsRequest,
  getDraftByParentIdRequest,
  discardDraftRequest,
  callViaApiService,
};

export type DraftRequestConfigType = Omit<RestApiRequestConfig<GetDraftsRequestBody>, 'requestBody' | 'headers' | 'queryParams'>;

export function getRequestConfig(httpMethod: Method, endpoint?: string): DraftRequestConfigType {
  return {
    restApiVersion: WorkbookDraftDraftRestApiVersion,
    httpMethod,
    endpoint: endpoint ? `workbookdrafts/${endpoint}`: 'workbookdrafts'
  };
}

export interface GetDraftsRequestParams {
  projectLuid: string;
}

export interface GetDraftsByIdRequestParams {
  parentRevisionId: string;
}

// When 'webAuthoringAutoSaveForNewWorkbooks' is enabled, containerLuid will be used
// for draft request query parameter generation. Otherwise, projectLuid will be used.
export interface GetDraftsRequestQueryParams {
  containerLuid?: string;
  projectLuid?: string;
}

export type QueryParams = GetDraftsRequestQueryParams | GetDraftsByIdRequestParams;

export interface DiscardDraftRequestParams {
  draftLuid: string;
}

export async function getDraftByParentIdRequest(action: FetchDraftByParentIdRequestedAction): Promise<Draft> {
  const { parentRevisionId } = action.data;
  const parentLuid = action.parentLuid;

  const config = getRequestConfig('GET', `findDraftByParentContent/${parentLuid}`);
  const queryParams = parentRevisionId ? {parentRevisionId} : undefined;
  const emptyBlob = getDraftsRequestBody();

  return DraftsRequests.callViaApiService<Draft>(emptyBlob, config, queryParams);
}

export async function getDraftsRequest(action: FetchDraftsRequestedAction): Promise<Draft[]> {
  const { projectLuid } = action.data;

  const requestQueryParams = getDraftsRequestQueryParam(projectLuid);
  const emptyBlob = getDraftsRequestBody();
  const config = getRequestConfig('GET');

  return DraftsRequests.callViaApiService<Draft[]>(emptyBlob, config, requestQueryParams);
}

export function discardDraftRequest(action: DiscardDraftRequestedAction): Promise<{}> {
  const initialModalInstance = openReactConfirmationDialog<string, {}>(discardDraftDialogOptions(action));
  return initialModalInstance.result;
}

// Separated for unit testing
export function discardDraftDialogOptions(action: DiscardDraftRequestedAction): ReactConfirmationDialogOptions<string, {}> {
  let dialogBody: string = Messages.confirmDeleteDraftWarning({
    DRAFT_CONTENT_TYPE: action.contentType,
    DRAFT_NAME: L10n.elideIfLong(action.name)
  });

  function onClickDiscard(draftLuid: string): PromiseLike<{}> {
    const config = getRequestConfig('DELETE', draftLuid);
    const emptyBlob = getDraftsRequestBody();

    const response = DraftsRequests.callViaApiService(emptyBlob, config);
    response.catch(() => {
      ToasterService.instance.pop({type: ToastType.Error, text: Messages.deleteDraftError()});
    });
    return response;
  }

  return {
    title: Messages.deleteMessage(),
    message: dialogBody,
    paramsForAction: action.draftLuid,
    actionName: Messages.deleteDraft(),
    action: onClickDiscard,
    isActionDelete: true,
    dialogWidth: TabDialogWidth.width375px
  };
}

export function callViaApiService<ReturnType>(
  body: GetDraftsRequestBody,
  requestConfig: DraftRequestConfigType,
  queryParams?: QueryParams,
  headers?: {[index: string]: string},
) {
  const config: RestApiRequestConfig<GetDraftsRequestBody, QueryParams> = {
    ...requestConfig,
    requestBody: body,
    headers,
    queryParams,
  };

  return makeRestApiRequest<Blob, ReturnType>(config);
}

function getDraftsRequestQueryParam(projectLuid: string): GetDraftsRequestQueryParams {
  //api/${WorkbookDraftDraftRestApiVersion}/siteluid/workbookdrafts?projectluid=something
  if (!projectLuid) {
    return undefined;
  }

  if (FeatureFlags.webAuthoringAutoSaveForNewWorkbooks) {
    return { containerLuid: projectLuid };
  } else {
    return { projectLuid };
  }
}

function getDraftsRequestBody(): Blob {
  //TODO I can probably get away without this. Confirm once I can run against the product
  return new Blob([]);
}
